import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { StoreItem } from './useData';

export const moduleConfig = {
  module: {
    title: 'Items',
    permissionModuleKey: 'store_items',
    route: {
      list: '/supplies/items',
      new: '/supplies/items/new',
      detail: (id: number) => '/supplies/items/' + id,
      edit: (id: number) => '/supplies/items/' + id + '/edit',
      history: (id: number) => '/supplies/items/' + id + '/history'},
    api: {
      list: '/store-items',
      detail: (id: number) => '/store-items/' + id,
      history: (id: number) => '/store-items/' + id + '/history'}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by name, vendor code or notes...' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['name', 'isActive']},
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'All names',
        getValue: (item: StoreItem) => item.name,
        options: []},
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: StoreItem) => (item.isActive ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ]},
    ],
    applyFilter: (items: StoreItem[], filters: Record<string, string[] | string>) => {
      const asArray = (v: string[] | string | undefined): string[] => (Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : []);
      return items.filter((item) => {
        const nameFilters = asArray(filters.name);
        if (nameFilters.length) {
          const v = item.name || '';
          if (!nameFilters.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }
        const activeFilters = asArray(filters.isActive);
        if (activeFilters.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!activeFilters.includes(s)) return false;
        }
        return true;
      });
    },
    /** Same fields as API list search: name, vendor code, notes, levels, unit code. */
    applySearch: (items: StoreItem[], term: string) => {
      const t = term.trim().toLowerCase();
      if (!t) return items;
      return items.filter((item) => {
        const fields = [
          item.name,
          item.vendorCode,
          item.notes,
          item.level1,
          item.level2,
          item.level3,
          item.unit?.ucode,
          item.catalog?.name,
        ];
        return fields.some((f) => (f ?? '').toLowerCase().includes(t));
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New item',
        showTitle: false,
        variant: 'primary' as const,
        href: '/supplies/items/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: StoreItem) => item.id,
  },
  emptyStates: {
    noData: 'Create an item to get started.',
    noSearchResults: 'No items match your search.',
    noFilterResults: 'No items match the filters.',
  },
  delete: {
    title: 'Delete item',
    message: 'Delete this item?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete item.',
    handler: async (id: number) => {
      const { deleteStoreItem } = await import('../../../services/mutations');
      await deleteStoreItem(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This item does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Item',
    getTitle: (item: StoreItem) => item.name || `Item #${item.id}`},
  form: {
    new: { title: 'New item', subtitle: 'Create a StoreItem record', submitText: 'Create' },
    edit: { title: 'Edit item', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<StoreItem>) => {
      const { createStoreItem } = await import('../../../services/mutations');
      return createStoreItem(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<StoreItem>) => {
      const { updateStoreItem } = await import('../../../services/mutations');
      return updateStoreItem(id, data as Record<string, unknown>);
    }},
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Item name',
      validation: { required: true, minLength: 1, maxLength: 200 }},
    {
      name: 'vendorCode',
      label: 'Vendor code',
      type: 'text',
      placeholder: 'Optional vendor code',
      validation: { maxLength: 20 }},
    {
      name: 'level1',
      label: 'Level 1',
      type: 'text',
      placeholder: 'e.g. category (IMPA)',
      validation: { maxLength: 200 }},
    {
      name: 'level2',
      label: 'Level 2',
      type: 'text',
      placeholder: 'Subcategory',
      validation: { maxLength: 200 }},
    {
      name: 'level3',
      label: 'Level 3',
      type: 'text',
      placeholder: 'Detail group',
      validation: { maxLength: 200 }},
    {
      name: 'unitId',
      label: 'Unit',
      type: 'select',
      placeholder: 'Select unit',
      apiEndpoint: '/store-units',
      valueField: 'id',
      labelField: 'descr',
      options: [{ value: '', label: 'No unit' }]},
    {
      name: 'catalogId',
      label: 'Catalog',
      type: 'select',
      placeholder: 'Select catalog',
      apiEndpoint: '/store-catalogs?page=1&limit=500',
      valueField: 'id',
      labelField: 'name',
      options: [{ value: '', label: 'No catalog' }]},
    {
      name: 'groupId',
      label: 'Group',
      type: 'select',
      placeholder: 'Select group',
      optionsEndpointFromFormData: (fd) => {
        const raw = fd.catalogId;
        const cid = raw === '' || raw === undefined || raw === null ? null : Number(raw);
        const base = '/store-item-groups?page=1&limit=500';
        if (cid != null && Number.isFinite(cid)) {
          return `${base}&catalogId=${encodeURIComponent(String(cid))}`;
        }
        return base;
      },
      optionsDependOnFields: ['catalogId'],
      valueField: 'id',
      labelField: 'name',
      options: [{ value: '', label: 'No group' }]},
    {
      name: 'stockMin',
      label: 'Minimum stock',
      type: 'number',
      placeholder: 'Optional minimum'},
    {
      name: 'stockMax',
      label: 'Maximum stock',
      type: 'number',
      placeholder: 'Optional maximum'},
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Optional notes'},
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Active',
      defaultValue: true},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: 'Item',
        icon: '',
        gridCols: 'md:grid-cols-12',
        fields: [
          'name',
          'vendorCode',
          'level1',
          'level2',
          'level3',
          'unitId',
          'catalogId',
          'groupId',
          'stockMin',
          'stockMax',
          'notes',
          'isActive',
        ],
        fieldSpans: {
          name: 'md:col-span-6',
          vendorCode: 'md:col-span-6',
          level1: 'md:col-span-4',
          level2: 'md:col-span-4',
          level3: 'md:col-span-4',
          unitId: 'md:col-span-6',
          catalogId: 'md:col-span-6',
          groupId: 'md:col-span-6',
          stockMin: 'md:col-span-6',
          stockMax: 'md:col-span-6',
          notes: 'md:col-span-12',
          isActive: 'md:col-span-12'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const LEVEL_CELL_MAX = 80;

const LevelTextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  const raw = value != null && value !== '' ? String(value) : '';
  const truncated = raw.length > LEVEL_CELL_MAX;
  const display = truncated ? `${raw.slice(0, LEVEL_CELL_MAX)}…` : raw;
  return (
    <span className={cn('text-sm block min-w-0 max-w-md truncate', colors.text)} title={truncated ? raw : undefined}>
      {display || '-'}
    </span>
  );
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<StoreItem>[] => [
  { key: 'level1', header: 'Level 1', sortable: true, render: (v) => <LevelTextCell value={v} /> },
  { key: 'level2', header: 'Level 2', sortable: true, render: (v) => <LevelTextCell value={v} /> },
  { key: 'level3', header: 'Level 3', sortable: true, render: (v) => <LevelTextCell value={v} /> },
  {
    key: 'catalogId',
    header: 'Catalog',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.catalog?.name ?? undefined} />},
  { key: 'vendorCode', header: 'Vendor code', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'unitId',
    header: 'Unit',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.unit?.ucode ?? undefined} />},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.isActive ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ?? row?.isActive ? 'Yes' : 'No'}
      </Badge>
    )},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: StoreItem, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      {
        label: 'Supply history',
        value: (
          <Link
            to={moduleConfig.module.route.history(item.id)}
            className="text-indigo-600 hover:text-indigo-800 hover:underline dark:text-indigo-400"
          >
            Requisitions, quotations, orders, deliveries, ports, prices…
          </Link>
        )},
      { label: 'Level 1', value: item.level1 || '' },
      { label: 'Level 2', value: item.level2 || '' },
      { label: 'Level 3', value: item.level3 || '' },
      { label: 'Vendor code', value: item.vendorCode || '' },
      { label: 'Name', value: item.name },
      { label: 'Unit', value: item.unit?.ucode ?? '' },
      { label: 'Catalog', value: item.catalog?.name ?? '—' },
      { label: 'Group ID', value: item.groupId ?? '' },
      { label: 'Stock min', value: item.stockMin ?? '' },
      { label: 'Stock max', value: item.stockMax ?? '' },
      { label: 'Notes', value: item.notes || '' },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ]},
];

const TableActionsCell: React.FC<{
  item: StoreItem;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.history(item.id)}
      className="p-1 text-amber-600 hover:text-amber-900 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="Supply history (ports, prices, suppliers)"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </Link>
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: StoreItem,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getStatusBadges = () => null;

export const getInitialFormData = (): Partial<StoreItem> => ({
  vendorCode: '',
  name: '',
  level1: '',
  level2: '',
  level3: '',
  unitId: undefined,
  catalogId: undefined,
  notes: '',
  isActive: true,
  groupId: undefined,
  stockMin: undefined,
  stockMax: undefined});

export const mapItemToFormData = (item: StoreItem): Partial<StoreItem> => ({
  vendorCode: item.vendorCode ?? '',
  name: item.name,
  level1: item.level1 ?? '',
  level2: item.level2 ?? '',
  level3: item.level3 ?? '',
  unitId: item.unitId ?? undefined,
  catalogId: item.catalogId ?? undefined,
  notes: item.notes ?? '',
  isActive: item.isActive ?? true,
  groupId: item.groupId ?? undefined,
  stockMin: item.stockMin ?? undefined,
  stockMax: item.stockMax ?? undefined});

export type ModuleConfig = typeof moduleConfig;
